import { GenericTableRowInterface, UserPropKey } from "@swivl-mono/types";
import { Button, ButtonColor, SearchInput, Spinner, TextArea } from "@swivl-mono/ui";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { UsersModel } from "../../../Models/Users/Users.Model";
import UserVarsModel from "../../../Models/Variables/UserVars.Model";
import { exportProfileCollectToCSV } from "../Helpers/CSVExportHelpers";
import { TableProfileCollectData, transformProfileCollectForTable } from "../Helpers/DataHelpers";
import "./ViewAllProfileCollect.scss";

interface ViewAllProfileCollectProps {
  rawProfileCollectData: GenericTableRowInterface[];
  onViewLess: () => void;
  locationNames: { [key: number]: string };
  startDay: string;
  endDay: string;
  onDateChange?: (startDay: string, endDay: string) => void;
}

export const ViewAllProfileCollect: React.FC<ViewAllProfileCollectProps> = ({
  rawProfileCollectData,
  onViewLess: _onViewLess,
  locationNames,
  startDay: _startDay,
  endDay: _endDay
}) => {
  const [tableData, setTableData] = useState<TableProfileCollectData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<keyof TableProfileCollectData>("updatedAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [hasUserSorted, setHasUserSorted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [usersLoadingProgress, setUsersLoadingProgress] = useState({ loaded: 0, total: 0, isComplete: false });

  // Track which users we've attempted to load vars for (from user_variable and user tables)
  const loadedUserVarsRef = useRef<Set<number>>(new Set());
  const loadedUsersRef = useRef<Set<number>>(new Set());

  // Get user data from UsersModel
  const { users } = UsersModel.useState();
  const { vars } = UserVarsModel.useState();

  // Transform and deduplicate raw data (single source of truth)
  const baseTableData = useMemo(() => {
    if (!rawProfileCollectData || !locationNames) return [];

    const transformed = transformProfileCollectForTable(rawProfileCollectData, locationNames);

    // Deduplicate by userId, keeping most recent
    const userMap = new Map<number, TableProfileCollectData>();
    transformed.forEach((item) => {
      const existing = userMap.get(item.userId);
      if (!existing || item.updatedAt > existing.updatedAt) {
        userMap.set(item.userId, item);
      }
    });

    return Array.from(userMap.values());
  }, [rawProfileCollectData, locationNames]);

  // Gradual user loading system
  useEffect(() => {
    if (baseTableData.length === 0) return;

    const allUserIds = baseTableData.map((item) => item.userId);
    const totalUsers = allUserIds.length;
    const loadedUserCount = users ? Object.keys(users).length : 0;
    const attemptedCount = loadedUsersRef.current.size;

    // Update progress tracking
    const isComplete = attemptedCount >= totalUsers;

    setUsersLoadingProgress((prev) => ({
      ...prev,
      total: totalUsers,
      loaded: loadedUserCount,
      isComplete
    }));

    // Don't load more if we're complete
    if (attemptedCount >= totalUsers) {
      return;
    }

    // Load in batches of 250 users - use attemptedCount not loadedUserCount!
    const batchSize = 250;
    const nextBatchEnd = Math.min(attemptedCount + batchSize, totalUsers);

    if (attemptedCount < nextBatchEnd) {
      const usersToLoad = allUserIds.slice(attemptedCount, nextBatchEnd).filter((userId) => !loadedUsersRef.current.has(userId));

      if (usersToLoad.length > 0) {
        usersToLoad.forEach((userId) => loadedUsersRef.current.add(userId));

        // Load with a slight delay to prevent overwhelming the API
        setTimeout(() => {
          UsersModel.loadUsersByIds(usersToLoad);
        }, 100);
      }
    }
  }, [baseTableData, users]);

  // Memoize enhance function to prevent recreation on every render
  const enhanceItem = useCallback(
    (item: TableProfileCollectData) => {
      const user = users?.[item.userId];
      const userVars = vars?.[item.userId] || {};

      return {
        ...item,
        name: user?.name || userVars[UserPropKey.Name] || "",
        email: user?.email || userVars[UserPropKey.Email] || "",
        phone: user?.phoneNumber || userVars[UserPropKey.PhoneNumber] || "",
        crmStatus: userVars["CRMStatus"] || "Pending",
        notes: userVars["Notes"] || ""
      };
    },
    [users, vars]
  );

  // Apply search filtering, enhancement, contactable filtering, sorting, and pagination
  const { displayData, totalContactableRecords, totalPages, allContactableRecords, searchedContactableRecords } = useMemo(() => {
    // 1. Enhance with user data first

    const enhanced = baseTableData.map(enhanceItem);

    // 2. Filter to contactable records only (only after some users are loaded)
    const hasUsersLoaded = users && Object.keys(users).length > 0;
    const contactableRecords = hasUsersLoaded
      ? enhanced.filter((item) => item.name.trim() !== "" || item.email.trim() !== "" || item.phone.trim() !== "")
      : enhanced;

    // 3. Apply search filter on enhanced data
    let searchFiltered = contactableRecords;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      searchFiltered = contactableRecords.filter(
        (row) =>
          row.name.toLowerCase().includes(query) ||
          row.email.toLowerCase().includes(query) ||
          row.phone.toLowerCase().includes(query) ||
          row.locationName.toLowerCase().includes(query) ||
          row.funnel.toLowerCase().includes(query) ||
          row.updatedAt.toLocaleDateString().toLowerCase().includes(query)
      );
    }

    // 4. Apply sorting (always sort, but only show active styling if user has sorted)
    let sortedRecords = [...searchFiltered];
    sortedRecords.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      // Handle different data types
      let comparison = 0;
      if (aVal instanceof Date && bVal instanceof Date) {
        comparison = aVal.getTime() - bVal.getTime();
      } else {
        const aStr = String(aVal || "").toLowerCase();
        const bStr = String(bVal || "").toLowerCase();
        comparison = aStr.localeCompare(bStr);
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    // 5. Calculate pagination
    const totalSearchResults = sortedRecords.length;
    const totalPagesCalc = Math.ceil(totalSearchResults / pageSize);

    // 6. Get current page data
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = sortedRecords.slice(startIndex, endIndex);

    return {
      displayData: paginatedData,
      totalContactableRecords: contactableRecords.length,
      searchedContactableRecords: totalSearchResults,
      totalPages: totalPagesCalc,
      allContactableRecords: sortedRecords // For CSV export
    };
  }, [baseTableData, searchQuery, users, vars, sortField, sortDirection, currentPage, pageSize, enhanceItem]);

  // Stop spinning when record count stops increasing
  useEffect(() => {
    const timeout = setTimeout(() => {
      setUsersLoadingProgress((prev) => ({ ...prev, isComplete: true }));
    }, 5000);
    return () => clearTimeout(timeout);
  }, [totalContactableRecords]);

  // Load users and user vars for records that need them
  useEffect(() => {
    if (displayData.length === 0) return;

    const userIds = displayData.map((item) => item.userId);

    // Load missing users
    const usersToLoad = userIds.filter((userId) => !loadedUsersRef.current.has(userId));
    if (usersToLoad.length > 0) {
      usersToLoad.forEach((userId) => loadedUsersRef.current.add(userId));
      UsersModel.loadUsersByIds(usersToLoad);
    }

    // Load missing user vars
    userIds.forEach((userId) => {
      if (!loadedUserVarsRef.current.has(userId)) {
        loadedUserVarsRef.current.add(userId);
        UserVarsModel.loadForUser(userId);
      }
    });
  }, [displayData]);

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    setTableData(displayData);
    if (displayData.length > 0) {
      setLoading(false);
    }
  }, [displayData]);

  const handleSort = (field: keyof TableProfileCollectData) => {
    setHasUserSorted(true);
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleExport = () => {
    // Export all contactable records currently being shown (after search and filtering)
    exportProfileCollectToCSV(allContactableRecords);
  };

  const handleRowUpdate = async (userId: number, field: string, value: string, shouldSave: boolean = false) => {
    // Update local state immediately
    setTableData((prev) => prev.map((row) => (row.userId === userId ? { ...row, [field]: value } : row)));

    // Save to user variables if shouldSave is true (for status auto-save or notes manual save)
    if (shouldSave) {
      try {
        const currentUserVars = vars?.[userId] || {};
        await UserVarsModel.saveUserVars(userId, {
          ...currentUserVars,
          [field]: value
        });
      } catch (error) {
        console.error("Failed to save user variable:", error);
      }
    }
  };

  if (loading) {
    return (
      <div className="ViewAllProfileCollect">
        <div className="loadingContainer">
          <Spinner />
          <div style={{ marginTop: "20px", fontSize: "16px", color: "#666" }}>Loading profile data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ViewAllProfileCollect">
      <div className="header">
        <Link to="/" className="backButton">
          ← Back
        </Link>
        <div className="headerActions">
          <Button color={ButtonColor.WHITE} onClick={handleExport} disabled={tableData.length === 0}>
            Export CSV
          </Button>
          <div className="searchInputWrapper">
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery("")}
              placeholder="Search..."
            />
          </div>
          <span className="body-sm shrink-0 font-semibold whitespace-nowrap text-grey-600">
            {usersLoadingProgress.isComplete ? (
              searchQuery ? (
                `${searchedContactableRecords} of ${totalContactableRecords} records`
              ) : (
                `${totalContactableRecords} records`
              )
            ) : (
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span>
                  {searchQuery ? `${searchedContactableRecords} of ${totalContactableRecords} records` : `${totalContactableRecords} records`}
                </span>
                <Spinner />
              </span>
            )}
          </span>
        </div>
      </div>

      <div className="tableContainer">
        <table className="profileCollectTable">
          <thead>
            <tr>
              <SortableHeader field="crmStatus" currentSort={sortField} direction={sortDirection} onSort={handleSort} hasUserSorted={hasUserSorted}>
                Status
              </SortableHeader>
              <SortableHeader field="notes" currentSort={sortField} direction={sortDirection} onSort={handleSort} hasUserSorted={hasUserSorted}>
                Notes
              </SortableHeader>
              <SortableHeader field="name" currentSort={sortField} direction={sortDirection} onSort={handleSort} hasUserSorted={hasUserSorted}>
                Name
              </SortableHeader>
              <SortableHeader field="email" currentSort={sortField} direction={sortDirection} onSort={handleSort} hasUserSorted={hasUserSorted}>
                Email
              </SortableHeader>
              <SortableHeader field="phone" currentSort={sortField} direction={sortDirection} onSort={handleSort} hasUserSorted={hasUserSorted}>
                Phone
              </SortableHeader>
              <SortableHeader field="funnel" currentSort={sortField} direction={sortDirection} onSort={handleSort} hasUserSorted={hasUserSorted}>
                Funnel
              </SortableHeader>
              <SortableHeader
                field="locationName"
                currentSort={sortField}
                direction={sortDirection}
                onSort={handleSort}
                hasUserSorted={hasUserSorted}
              >
                Location
              </SortableHeader>
              <th>Conversation</th>
              <SortableHeader field="updatedAt" currentSort={sortField} direction={sortDirection} onSort={handleSort} hasUserSorted={hasUserSorted}>
                Updated
              </SortableHeader>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="loadingData">
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "20px" }}>
                    <Spinner />
                    <span style={{ marginLeft: "10px" }}>Loading user data...</span>
                  </div>
                </td>
              </tr>
            ) : tableData.length === 0 ? (
              <tr>
                <td colSpan={9} className="noData">
                  No profile data available with the current filters.
                </td>
              </tr>
            ) : (
              tableData.map((row) => <TableRow key={row.id} data={row} onUpdate={handleRowUpdate} />)
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="paginationContainer">
          <div className="paginationControls">
            <Button color={ButtonColor.BLACK} onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
              First
            </Button>
            <Button color={ButtonColor.BLACK} onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))} disabled={currentPage === 1}>
              Previous
            </Button>

            <span className="pageInfo">
              Page {currentPage} of {totalPages}
            </span>

            <Button
              color={ButtonColor.BLACK}
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
            <Button color={ButtonColor.BLACK} onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
              Last
            </Button>
          </div>

          <div className="pageSizeSelector">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="pageSizeDropdown"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span> records per page</span>
          </div>
        </div>
      )}
    </div>
  );
};

interface TableRowProps {
  data: TableProfileCollectData;
  onUpdate: (userId: number, field: string, value: string, shouldSave?: boolean) => void;
}

const TableRow: React.FC<TableRowProps> = ({ data, onUpdate }) => {
  return (
    <tr>
      <td className="statusCell">
        <StatusDropdown value={data.crmStatus} userId={data.userId} onUpdate={onUpdate} />
      </td>
      <td className="notesCell">
        <NotesField value={data.notes} userId={data.userId} onUpdate={onUpdate} />
      </td>
      <td className="nameCell">
        <span className="value">{data.name || "-"}</span>
      </td>
      <td className="emailCell">
        <span className="value">{data.email || "-"}</span>
      </td>
      <td className="phoneCell">
        <span className="value">{data.phone || "-"}</span>
      </td>
      <td className="funnelCell">{data.funnel}</td>
      <td className="locationCell">{data.locationName}</td>
      <td className="conversationCell">
        <a href={data.conversationLink} target="_blank" rel="noopener noreferrer" className="conversationLink" title="View Conversation">
          💬
        </a>
      </td>
      <td className="updatedCell">
        {data.updatedAt.toLocaleDateString()} {data.updatedAt.toLocaleTimeString()}
      </td>
    </tr>
  );
};

interface StatusDropdownProps {
  value: string;
  userId: number;
  onUpdate: (userId: number, field: string, value: string, shouldSave?: boolean) => void;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({ value, userId, onUpdate }) => {
  const statusOptions = [
    { value: "Pending", label: "⏳ Pending" },
    { value: "In Progress", label: "🔄 In Progress" },
    { value: "On Hold", label: "⏸️ On Hold" },
    { value: "Call Back", label: "📞 Call Back" },
    { value: "Completed", label: "✅ Completed" }
  ];

  return (
    <select
      value={value}
      onChange={(e) => onUpdate(userId, "CRMStatus", e.target.value, true)} // Auto-save status changes
      className="statusDropdown"
    >
      {statusOptions.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

interface NotesFieldProps {
  value: string;
  userId: number;
  onUpdate: (userId: number, field: string, value: string, shouldSave?: boolean) => void;
}

const NotesField: React.FC<NotesFieldProps> = ({ value, userId, onUpdate }) => {
  const [localValue, setLocalValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local value when props change (for real-time updates from other users)
  useEffect(() => {
    setLocalValue(value);
    setHasChanges(false);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalValue(e.target.value);
    setHasChanges(e.target.value !== value);
  };

  const handleSave = () => {
    if (hasChanges) {
      onUpdate(userId, "Notes", localValue, true); // Save to database
      setHasChanges(false);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setLocalValue(value); // Reset to original value
    setHasChanges(false);
    setIsEditing(false);
  };

  return (
    <div className="notesFieldContainer">
      <TextArea
        value={localValue}
        onChange={handleChange}
        onFocus={() => setIsEditing(true)}
        className={`notesField ${isEditing ? "editing" : ""}`}
        rows={2}
        placeholder="Add notes..."
      />
      {isEditing && (
        <div className="notesActions">
          <button className="saveButton" onClick={handleSave} disabled={!hasChanges}>
            Save
          </button>
          <button className="cancelButton" onClick={handleCancel}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

interface SortableHeaderProps {
  field: keyof TableProfileCollectData;
  currentSort: keyof TableProfileCollectData;
  direction: "asc" | "desc";
  onSort: (field: keyof TableProfileCollectData) => void;
  hasUserSorted: boolean;
  children: React.ReactNode;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({ field, currentSort, direction, onSort, hasUserSorted, children }) => {
  const isActive = hasUserSorted && currentSort === field;

  return (
    <th className={`sortable ${isActive ? "active" : ""}`} onClick={() => onSort(field)}>
      {children}
      <span className="sortIcon">{isActive ? (direction === "asc" ? "↑" : "↓") : "↕"}</span>
    </th>
  );
};
